import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { googleAdsApi } from '@/services/api';
import {
  MapPinOff, Loader2, ArrowUpRight, ArrowDownRight, Minus, Sparkles,
  AlertTriangle, HelpCircle, Flame, ChevronDown, ChevronRight,
} from 'lucide-react';

const inr = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;
const num = (v) => Number(v || 0).toLocaleString('en-IN');
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mm] = m.split('-');
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(mm) - 1]} ${y.slice(2)}`;
};

// Heat is relative to the busiest city, so the widest bar is always "the most demand"
// rather than an absolute scale nobody can calibrate.
const heatTone = (i) => (i >= 0.66 ? 'bg-rose-500' : i >= 0.33 ? 'bg-amber-500' : 'bg-sky-400');

const HeatBar = ({ intensity, testId }) => (
  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-testid={testId}>
    <div
      className={`h-full rounded-full transition-[width] duration-500 ${heatTone(intensity)}`}
      style={{ width: `${Math.max(Number(intensity || 0) * 100, intensity > 0 ? 6 : 0)}%` }}
    />
  </div>
);

const TrendCell = ({ months = [], trend, trendPct, testId }) => {
  const peak = Math.max(...months.map((m) => m.leads), 1);
  const Icon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus;
  const tone = trend === 'up' ? 'text-rose-600' : trend === 'down' ? 'text-emerald-600' : 'text-gray-400';
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="flex items-end gap-0.5 h-6" title={months.map((m) => `${monthLabel(m.month)}: ${m.leads}`).join(' · ')}>
        {months.map((m) => (
          <div key={m.month} className="w-1.5 bg-slate-300 rounded-sm"
               style={{ height: `${Math.max((m.leads / peak) * 100, 8)}%` }} />
        ))}
      </div>
      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${tone}`}>
        <Icon className="h-3.5 w-3.5" />
        {trend === 'new' ? 'new' : trendPct === null || trendPct === undefined
          ? '—' : `${trendPct > 0 ? '+' : ''}${trendPct}%`}
      </span>
    </div>
  );
};

const Tile = ({ label, value, sub, testId }) => (
  <div className="rounded-lg border bg-slate-50/60 p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const Th = ({ children, right }) => (
  <th className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);
const Td = ({ children, right, className = '' }) => (
  <td className={`px-4 py-2.5 text-sm text-gray-700 ${right ? 'text-right' : ''} ${className}`}>{children}</td>
);

export const UnservicedDemandCard = ({ from, to }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { date_from: from, date_to: to };
    try {
      // First paint deliberately skips Meta. Meta has no stored per-day spend, so it is
      // a LIVE insights call that adds seconds to a column which only annotates a row —
      // Google's numbers are the point of the card and must not wait behind it.
      const fast = await googleAdsApi.getUnservicedDemand({ ...params, include_meta: false });
      setData(fast.data);
      setLoading(false);
      // Then fill Meta in the background. A failure here must never replace numbers
      // that already rendered.
      try {
        const full = await googleAdsApi.getUnservicedDemand({ ...params, include_meta: true });
        setData(full.data);
      } catch {
        /* keep the Google-only view */
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load unserviced-city demand');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const rows = data?.rows || [];
  const noCity = data?.no_city || {};
  const wasted = useMemo(() => rows.filter((r) => r.spend_without_leads), [rows]);

  if (loading && !data) {
    return (
      <div className="bg-white rounded-xl border mb-6 p-5 flex items-center gap-2 text-sm text-gray-500"
           data-testid="unserviced-demand-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        Working out where the demand we cannot serve is coming from…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border mb-6 overflow-hidden" data-testid="unserviced-demand-card">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="w-full px-5 py-4 border-b bg-gradient-to-r from-rose-50/70 to-white flex items-start justify-between gap-4 text-left hover:from-rose-50 transition-colors"
              data-testid="unserviced-demand-toggle">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 h-8 w-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
            <MapPinOff className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Demand from cities we don&apos;t serve yet
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Car-service enquiries from places car service is not live in — and what we
              already paid to get them. Read-only; launching a city stays a deliberate decision.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          <span className="hidden md:inline text-[11px] text-gray-400">
            Live in {(data?.serviceable_cities || []).join(' · ') || '—'}
          </span>
          {open ? <ChevronDown className="h-4 w-4 text-gray-400" />
                : <ChevronRight className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className={`transition-opacity ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
          {error && (
            <div className="m-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                 data-testid="unserviced-demand-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-5">
            <Tile label="Enquiries we can't serve" value={num(t.unserviced_leads)}
                  sub={`${t.cities || 0} ${t.cities === 1 ? 'city' : 'cities'} · ${num(t.whatsapp)} on WhatsApp`}
                  testId="unserviced-demand-total" />
            <Tile label="No city captured" value={num(t.no_city_leads)}
                  sub={t.missed_share_pct !== null && t.missed_share_pct !== undefined
                    ? `${t.missed_share_pct}% of all enquiries have nowhere to go`
                    : 'tracking gap, not geography'}
                  testId="unserviced-demand-nocity-tile" />
            <Tile label="Google spend there" value={inr(t.spend)}
                  sub={`${num(t.clicks)} clicks${t.meta_spend ? ` · Meta ${inr(t.meta_spend)}` : ''}`}
                  testId="unserviced-demand-spend" />
            <Tile label="Cost per unserviced lead" value={t.cost_per_lead ? inr(t.cost_per_lead) : '—'}
                  sub={t.wasted_spend ? `${inr(t.wasted_spend)} bought nothing at all` : 'spend ÷ enquiries we can\'t serve'}
                  testId="unserviced-demand-cpl" />
          </div>

          {noCity.leads > 0 && (
            <div className="mx-5 mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-3"
                 data-testid="unserviced-demand-nocity-banner">
              <HelpCircle className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-700 leading-relaxed">
                <span className="font-semibold">{num(noCity.leads)} enquiries arrived with no city at all</span>
                {' '}— {num(noCity.whatsapp)} on WhatsApp, {num(noCity.website)} from the website,
                {' '}{num(noCity.manual)} added by hand. They are kept out of the table below
                because &quot;we don&apos;t know where they are&quot; is a tracking gap, not demand
                from a known place. WhatsApp carries no city, and the landing page posted an
                empty one until the <code className="px-1 bg-white rounded border">?city=</code> slug
                was captured.
                {noCity.google_leads > 0 && (
                  <> <span className="font-medium">{num(noCity.google_leads)} of them were paid Google clicks.</span></>
                )}
              </div>
            </div>
          )}

          {wasted.length > 0 && (
            <div className="mx-5 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
                 data-testid="unserviced-demand-waste-banner">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <span className="font-semibold">{inr(t.wasted_spend)} went to {wasted.length}
                  {wasted.length === 1 ? ' city' : ' cities'} that produced no enquiry at all</span>
                {' '}— {wasted.map((r) => r.city).join(', ')}. Either the targeting is leaking
                outside the cities we serve, or the landing page is losing those visitors.
              </div>
            </div>
          )}

          {rows.length === 0 && !error ? (
            <div className="px-5 pb-6 text-sm text-gray-500" data-testid="unserviced-demand-empty">
              <Sparkles className="h-4 w-4 inline mr-1.5 text-emerald-500" />
              Every car-service enquiry in this window came from a city we already serve.
            </div>
          ) : (
            <div className="overflow-x-auto border-t">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>City</Th>
                    <Th>Demand</Th>
                    <Th right>Enquiries</Th>
                    <Th right>WhatsApp</Th>
                    <Th right>Website</Th>
                    <Th right>Paid clicks</Th>
                    <Th right>Google spend</Th>
                    <Th right>₹ / enquiry</Th>
                    <Th>Last 3 months</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <tr key={r.city}
                        className={`hover:bg-gray-50 ${r.spend_without_leads ? 'bg-amber-50/50' : ''} ${r.below_bar ? 'text-gray-400' : ''}`}
                        data-testid={`unserviced-city-row-${r.city}`}>
                      <Td className={`whitespace-nowrap ${r.below_bar ? '' : 'font-medium'}`}>
                        <span className="inline-flex items-center gap-1.5"
                              title={r.below_bar ? (r.names || []).join(', ') : undefined}>
                          {!r.below_bar && r.intensity >= 0.66 && (
                            <Flame className="h-3.5 w-3.5 text-rose-500" />
                          )}
                          {r.city}
                          {r.below_bar && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                              below the bar
                            </span>
                          )}
                        </span>
                        {!r.below_bar && r.out_of_area > 0 && (
                          <span className="ml-2 text-[10px] text-gray-400">
                            {num(r.out_of_area)} closed as out of area
                          </span>
                        )}
                      </Td>
                      <Td className="min-w-[140px]">
                        {r.below_bar
                          ? <span className="text-[11px] text-gray-400">grouped</span>
                          : <HeatBar intensity={r.intensity} testId={`unserviced-heat-${r.city}`} />}
                      </Td>
                      <Td right className={r.below_bar ? '' : 'font-semibold'}>{num(r.leads)}</Td>
                      <Td right>{num(r.whatsapp)}</Td>
                      <Td right>{num(r.website)}</Td>
                      <Td right>
                        {num(r.google_leads)}
                        {r.paid_share_pct ? (
                          <span className="text-[10px] text-gray-400 ml-1">({r.paid_share_pct}%)</span>
                        ) : null}
                      </Td>
                      <Td right>
                        {r.spend ? inr(r.spend) : '—'}
                        {r.meta_spend > 0 && (
                          <div className="text-[10px] text-gray-400">+{inr(r.meta_spend)} Meta</div>
                        )}
                      </Td>
                      <Td right>{r.cost_per_lead ? inr(r.cost_per_lead) : '—'}</Td>
                      <Td>
                        <TrendCell months={r.months} trend={r.trend} trendPct={r.trend_pct}
                                   testId={`unserviced-trend-${r.city}`} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-5 py-3 border-t bg-gray-50/60 text-[11px] text-gray-500 leading-relaxed">
            Suburbs roll into their metro, so Thane counts as Mumbai and Secunderabad as
            Hyderabad. Spend is the city Google billed us for, taken from the same
            definition as the tables above.
            {data?.totals?.minor_cities > 0 && (
              <> {data.totals.minor_cities} {data.totals.minor_cities === 1 ? 'name' : 'names'} with
                fewer than {data?.min_leads || 2} enquiries and no spend are grouped into one
                row — hover it to see them.</>
            )}
            {data?.spend_city_unreported > 0 && (
              <> {inr(data.spend_city_unreported)} of spend carried no city from Google and is
                not spread across these rows.</>
            )}
            {data?.meta_spend && !data.meta_spend.available && (
              <> Meta spend is not included: {data.meta_spend.reason}.</>
            )}
            {' '}The arrow compares the last two calendar months over a rolling 90 days, so
            it does not move when you change the date range.
          </div>
        </div>
      )}
    </div>
  );
};

export default UnservicedDemandCard;
